import { createEventEmitter, type Event } from "@core/Event/EventEmitter";
import Path from "@core/FileProvider/Path/Path";
import type PathnameData from "@core/RouterPath/model/PathnameData";
import { ContentLanguage } from "@ext/localization/core/model/Language";

// biome-ignore lint/complexity/noStaticOnlyClass: it's ok
class RouterPathProvider {
	static readonly events = createEventEmitter<
		Event<"parse-path", { segments: string[]; mutable: { data?: PathnameData } }> &
			Event<"is-editor-path", { segments: string[]; mutable: { value: boolean } }> &
			Event<"generate-path", { data: PathnameData; mutable: { path: Path } }> &
			Event<"unresolved-path", { path: string; mutable: { handled?: boolean } }>
	>();
	private static readonly _separator = "-";
	private static readonly _readonlyPathPrefix = "/";

	static getLogicPath(pathname: string) {
		const data = RouterPathProvider.isEditorPathname(pathname)
			? new Path(RouterPathProvider.parsePath(new Path(pathname)).itemLogicPath).value
			: pathname.startsWith(RouterPathProvider._readonlyPathPrefix)
				? pathname.substring(1)
				: pathname;
		return data;
	}

	static getReadOnlyPathname(ligicPath: string) {
		return `${RouterPathProvider._readonlyPathPrefix}${ligicPath}`;
	}

	static parsePath(path: string[] | string | Path): PathnameData {
		const mutable: { data?: PathnameData } = {};
		RouterPathProvider.events.emitSync("parse-path", {
			segments: RouterPathProvider._getArrayOfStrings(path),
			mutable,
		});
		return mutable.data ?? RouterPathProvider.parseEditorPath(RouterPathProvider._parseSegments(path));
	}

	static parseEditorPath(path: string[] | string | Path): PathnameData {
		const segments = RouterPathProvider._getArrayOfStrings(path).map((p) => (p === "-" ? null : p));

		let isPublic = false;
		if (segments[0] === "public") {
			isPublic = true;
			segments.shift();
		}

		const [sourceName, group, repo, refname, dir, maybeLanguage, ...filePath] = segments;

		const language = ContentLanguage[maybeLanguage];
		maybeLanguage && filePath.unshift(maybeLanguage);
		const normalizedFilePath = filePath.map((x) => decodeURIComponent(x));
		const catalogName = dir ?? repo ?? RouterPathProvider._separator;
		const itemLogicPath = [catalogName, ...normalizedFilePath];
		const repNameItemLogicPath = repo ? [repo, ...normalizedFilePath] : null;

		const hash = catalogName?.split("#", 1)?.[1] ?? "";

		return {
			sourceName: sourceName ? decodeURIComponent(sourceName) : null,
			group: group ? decodeURIComponent(group) : null,
			repo: repo,
			refname: refname ? decodeURIComponent(refname) : null,
			catalogName: catalogName === RouterPathProvider._separator ? null : catalogName,
			language,
			filePath: normalizedFilePath,
			itemLogicPath,
			repNameItemLogicPath,
			hash,
			isPublic,
		};
	}

	static getPathname(data: PathnameData): Path {
		const hasItemLogicPath = data.itemLogicPath?.length > 0;
		const hasFilePath = data.filePath?.length > 0;
		let catalogName = data.catalogName ?? (hasItemLogicPath ? data.itemLogicPath[0] : null);
		const filePath = hasFilePath
			? new Path(data.filePath).value
			: hasItemLogicPath
				? new Path(data.itemLogicPath.slice(1)).value
				: null;
		catalogName = catalogName === data.repo ? RouterPathProvider._separator : catalogName;

		const source = data.sourceName ? encodeURIComponent(data.sourceName) : RouterPathProvider._separator;

		const path = new Path([
			source,
			data.group ? encodeURIComponent(data.group) : RouterPathProvider._separator,
			data.repo ?? RouterPathProvider._separator,
			data.refname ? encodeURIComponent(data.refname) : RouterPathProvider._separator,
			catalogName,
			filePath,
		]);
		const mutable = { path };
		RouterPathProvider.events.emitSync("generate-path", { data, mutable });
		return mutable.path;
	}

	static parseItemLogicPath(itemLogicPath: string[] | Path): {
		catalogName: string;
		filePath: string[];
		fullPath: string[];
	} {
		const fullPath = RouterPathProvider._getArrayOfStrings(itemLogicPath);
		const [catalogName, ...filePath] = fullPath;
		return { catalogName, filePath, fullPath };
	}

	static isEditorPathname(path: string[] | string | Path): boolean {
		const currentPath = RouterPathProvider._getArrayOfStrings(path);
		const exclude = ["public"];
		const offset = exclude.includes(currentPath[0]) ? 1 : 0;

		const maybeStorage = decodeURIComponent(currentPath[0 + offset]);
		const maybeSeparator = currentPath
			.slice(1 + offset, 4 + offset)
			.some((s) => s === RouterPathProvider._separator);

		// `host:port` (e.g. an internal Gitea reached by a short DNS alias like `gitea-server:3000`)
		// is a valid Git host even without a dot — recognize the general shape, not only `localhost`.
		const hasPort = /^.*:\d+$/.test(maybeStorage);

		const isEditorPathname =
			(maybeStorage?.includes(".") || maybeSeparator || maybeStorage.startsWith("localhost") || hasPort) &&
			(!maybeStorage?.includes(":") || hasPort);

		const mutable = { value: isEditorPathname };
		RouterPathProvider.events.emitSync("is-editor-path", { segments: currentPath, mutable });
		return mutable.value;
	}

	static updatePathnameData(
		basePathname: PathnameData | string[] | string | Path,
		newPathnameData: PathnameData,
	): Path {
		const pathnameData =
			Array.isArray(basePathname) || basePathname instanceof Path || typeof basePathname === "string"
				? RouterPathProvider.parsePath(basePathname)
				: basePathname;
		return RouterPathProvider.getPathname({ ...pathnameData, ...newPathnameData });
	}

	static validate(pathdata: PathnameData): boolean {
		return !!(pathdata.sourceName && pathdata.group && pathdata.repo);
	}

	static isLocal(pathdata: PathnameData): boolean {
		return !pathdata.sourceName && !pathdata.group && !pathdata.repo;
	}

	private static _getArrayOfStrings(path: string[] | string | Path): string[] {
		const exclude = ["http:", "https:", "tauri:"];

		const arr = Array.isArray(path)
			? path
			: path
					.toString()
					.split("/")
					.filter((f) => f !== "");

		if (exclude.includes(arr[0])) {
			// Desktop (Tauri) hrefs carry the app origin `tauri://localhost/...`; strip the
			// `localhost` authority together with the scheme so the remaining segments are the
			// editor pathname (matching the web/relative form). Web `localhost:<port>` sources
			// keep their port and are not affected.
			if (arr[0] === "tauri:" && arr[1] === "localhost") arr[1] = null;
			arr[0] = null;
		}
		if (exclude.includes(arr[1])) arr[1] = null;

		return arr.filter(Boolean);
	}

	private static _parseSegments(path: string[] | string | Path): string[] {
		const rawSegments = RouterPathProvider._getArrayOfStrings(path);
		const segments = RouterPathProvider.isEditorPathname(rawSegments)
			? rawSegments
			: RouterPathProvider._getArrayOfStrings(RouterPathProvider.getPathname({ itemLogicPath: rawSegments }));

		return segments;
	}
}

export default RouterPathProvider;
